import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Paper,
  ListItemButton
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CodeIcon from '@mui/icons-material/Code';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import InputBase from '@mui/material/InputBase';
import { useWebSocket } from './WebSocketContext';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import CloseButton from './Common/CloseButton';

const Breadcrumb = ({ path, onNavigate, onAddFolder, onPathChange, onExitEditMode, displayRoot = '' }) => {
  const isWindows = typeof navigator !== 'undefined' && /windows/i.test(navigator.userAgent);
  const [editMode, setEditMode] = useState(false);
  const [inputValue, setInputValue] = useState(path || '');
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const prevEditMode = useRef(editMode);
  
  // Helper to get display path (hide displayRoot prefix)
  const getDisplayPath = (fullPath) => {
    if (!displayRoot || !fullPath) return fullPath;
    const normalizeForDisplay = (p) => p.replace(/\\/g, '/').toLowerCase();
    const normalizedFull = normalizeForDisplay(fullPath);
    const normalizedRoot = normalizeForDisplay(displayRoot);
    
    if (normalizedFull === normalizedRoot) {
      return '/'; // At root, show just /
    }
    if (normalizedFull.startsWith(normalizedRoot + '/')) {
      // Remove the root prefix, keep the rest
      const result = fullPath.substring(displayRoot.length);
      return result;
    }
    return fullPath;
  };

  // Convert display path (relative) back to full path
  const getFullPath = (displayPathInput) => {
    if (!displayRoot || !displayPathInput) return displayPathInput;
    // If it's just '/' or starts with '/', prepend displayRoot
    if (displayPathInput === '/' || displayPathInput === '\\') {
      return displayRoot;
    }
    // If it starts with / or \, it's relative to displayRoot
    if (displayPathInput.startsWith('/') || displayPathInput.startsWith('\\')) {
      return displayRoot + displayPathInput;
    }
    // Otherwise assume it's already a full path
    return displayPathInput;
  };

  useEffect(() => {
    if (editMode && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(inputValue.length, inputValue.length);
    }
  }, [editMode, inputValue]);

  useEffect(() => {
    // Only update the input value from the prop when not actively editing
    // This prevents incoming server updates from clobbering the user's in-progress edits
    if (!editMode) {
      const displayPathForInput = getDisplayPath(path);
      setInputValue(displayPathForInput || '');
    }
  }, [path, getDisplayPath, editMode]);

  // Detect editMode transition from true to false
  useEffect(() => {
    if (prevEditMode.current && !editMode) {
      // editMode changed from true to false
      if (onExitEditMode) {
        onExitEditMode(inputValue);
      }
    }
    prevEditMode.current = editMode;
  }, [editMode, inputValue, onExitEditMode]);

  // Click outside to exit edit mode
  useEffect(() => {
    if (!editMode) return;
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setEditMode(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editMode]);

  // Add this effect to notify parent of input changes
  useEffect(() => {
    onPathChange(inputValue);
  }, [inputValue, onPathChange]);

  const getPathParts = (path) => {
    if (!path) return [];
    if (isWindows) {
      const parts = path.split(/[\\/]/).filter(Boolean);
      if (parts.length > 0 && /^[A-Za-z]:$/.test(parts[0])) {
        return parts;
      }
    }
    return path.split('/').filter(Boolean);
  };

  const buildPath = (parts, index) => {
    if (isWindows) {
      if (index === 0) return parts[0];
      return parts.slice(0, index + 1).join('\\');
    }
    return '/' + parts.slice(0, index + 1).join('/');
  };

  // Calculate display path and parts for rendering
  const displayPath = getDisplayPath(path);
  const parts = getPathParts(displayPath);

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      setEditMode(false);
      if (inputValue !== path) {
        // Convert display path (relative) to full path before navigating
        const fullPath = getFullPath(inputValue);
        onNavigate(fullPath);
      }
    }
  };

  const handleGoClick = () => {
    setEditMode(false);
    if (inputValue !== path) {
      // Convert display path (relative) to full path before navigating
      const fullPath = getFullPath(inputValue);
      onNavigate(fullPath);
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        margin: '8px 0',
      }}
    >
      <nav
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          fontSize: '1.15rem',
          color: '#6b7280',
          padding: '6px 16px',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          background: '#fafbfc',
          minHeight: '40px',
          boxSizing: 'border-box',
          wordBreak: 'break-all',
          flex: '1 1 auto',
          cursor: 'text'
        }}
        onClick={(e) => {
          // Only enable edit mode if the click wasn't on a button
          if (!e.target.closest('button')) {
            setEditMode(true);
            setTimeout(() => {
              if (inputRef.current) {
                inputRef.current.focus();
                // inputRef.current.setSelectionRange(inputValue.length, inputValue.length);
              }
            }, 0);
          }
        }}
      >
        {!editMode ? (
          <>
            {/* Show Root or folder name at root level */}
            {parts.length === 0 || (displayPath === '/' || displayPath === '\\') ? (
              <span style={{ color: '#333', fontWeight: 600 }}>Root</span>
            ) : (
              <>
                <button
                  onClick={() => {
                    // If displayRoot is set, navigate to it, otherwise to '/'
                    if (displayRoot) {
                      onNavigate(displayRoot);
                    } else {
                      onNavigate('/');
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6b7280',
                    cursor: 'pointer',
                    padding: 0,
                    fontWeight: 400,
                    textDecoration: 'none',
                    fontSize: 'inherit',
                  }}
                  onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}
                >
                  Root
                </button>
                {parts.map((part, index) => (
                  <React.Fragment key={index}>
                    <NavigateNextIcon sx={{ fontSize: '1.2rem', mx: '0.5rem', color: '#b0b0b0' }} />
                    <button
                      onClick={() => {
                        // Build full path from displayRoot + relative path
                        const relativePath = buildPath(parts, index);
                        const fullPath = displayRoot ? displayRoot + relativePath : relativePath;
                        onNavigate(fullPath);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: index === parts.length - 1 ? '#333' : '#6b7280',
                        cursor: index === parts.length - 1 ? 'default' : 'pointer',
                        padding: 0,
                        fontWeight: index === parts.length - 1 ? 600 : 400,
                    textDecoration: 'none',
                    fontSize: 'inherit',
                  }}
                  onMouseOver={e => { if (index !== parts.length - 1) e.currentTarget.style.textDecoration = 'underline'; }}
                  onMouseOut={e => { if (index !== parts.length - 1) e.currentTarget.style.textDecoration = 'none'; }}
                  disabled={index === parts.length - 1}
                >
                  {part}
                </button>
              </React.Fragment>
            ))}
              </>
            )}
          </>
        ) : (
          <InputBase
            inputRef={inputRef}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onBlur={() => setEditMode(false)}
            onKeyDown={handleInputKeyDown}
            sx={{
              fontSize: '1.15rem',
              minWidth: 120,
              maxWidth: '100%',
              flex: 1,
              ml: 1,
            }}
            fullWidth={false}
          />
        )}
      </nav>
      {editMode && (
        <IconButton
          size="small"
          sx={{ ml: 2, p: '4px', height: 28, width: 28 }}
          onClick={handleGoClick}
        >
          <KeyboardReturnIcon fontSize="small" />
        </IconButton>
      )}
      <IconButton
        size="small"
        sx={{ ml: 1, p: '4px', height: 28, width: 28 }}
        onClick={onAddFolder}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

const FolderBrowser = ({ open, onClose, onSelect, restrictToUserFolder = false, allowedRoots = [], displayRoot = '', allowFileSelection = false }) => {
  const { t } = useTranslation();
  const { ws, subscribeToMessages } = useWebSocket();
  const [currentPath, setCurrentPath] = useState('');
  const [displayPath, setDisplayPath] = useState('');
  const [inputPath, setInputPath] = useState('');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [isAddingFolder, setIsAddingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('New Folder');
  const [editingFolder, setEditingFolder] = useState(null);
  const [editFolderName, setEditFolderName] = useState('');
  const [isValidatingPath, setIsValidatingPath] = useState(false);
  const [pendingSelect, setPendingSelect] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const editFolderInputRef = useRef(null);
  const newFolderInputRef = useRef(null);

  // Convert a display path (which may be relative to displayRoot) into a full path
  const resolveDisplayPath = useCallback((p) => {
    if (!displayRoot) return p;
    if (!p) return displayRoot;
    if (p === '/' || p === '\\') return displayRoot;
    if (p.startsWith('/') || p.startsWith('\\')) return displayRoot + p;
    return p;
  }, [displayRoot]);

  // Helper function to check if path is allowed
  const isPathAllowed = useCallback((path) => {
    if (!restrictToUserFolder || !allowedRoots || allowedRoots.length === 0) {
      return true;
    }
    
    // Normalize paths for comparison (handle both Windows and Unix separators)
    const normalizePath = (p) => {
      if (!p) return '';
      return p.replace(/\\/g, '/').toLowerCase();
    };
    
    const normalizedPath = normalizePath(path);
    
    // Check if path is under any allowed root
    for (const root of allowedRoots) {
      const normalizedRoot = normalizePath(root);
      if (normalizedPath === normalizedRoot || normalizedPath.startsWith(normalizedRoot + '/')) {
        return true;
      }
    }
    
    return false;
  }, [restrictToUserFolder, allowedRoots]);

  // Theme colors for consistent styling (copied from SettingFoldersConnector)
  const themeColors = {
    primary: '#5757b0',
    primaryLight: '#7878c5',
    primaryDark: '#3a3a8c',
    accent: '#ff8a65',
    text: '#333',
    lightText: '#f5f5f5',
    background: '#f8f9fa',
    cardBackground: '#ffffff'
  };

  useEffect(() => {
    if (open && ws && ws.readyState === WebSocket.OPEN) {
      // If restricted, start in the first allowed root
      if (restrictToUserFolder && allowedRoots && allowedRoots.length > 0) {
        setDisplayPath('/');
        setInputPath('/');
        // Directly send the command instead of using listDirectory to avoid callback issues
        setLoading(true);
        ws.send(JSON.stringify({ command: 'listDirectory', path: allowedRoots[0] }));
      } else {
        const isWindows = typeof navigator !== 'undefined' && /windows/i.test(navigator.userAgent);
        const initialPath = isWindows ? '~' : '~';
        setLoading(true);
        ws.send(JSON.stringify({ command: 'listDirectory', path: initialPath }));
      }
    }
  }, [open, ws, restrictToUserFolder, allowedRoots]);

  const listDirectory = useCallback((path) => {
    // Check if path is allowed before navigating
    if (restrictToUserFolder) {
      const resolvedPath = resolveDisplayPath(path);
      if (resolvedPath !== '/' && resolvedPath !== '~' && !isPathAllowed(resolvedPath)) {
        setError(t('folder_browser.error_access_denied'));
        setLoading(false);
        return;
      }
      path = resolvedPath;
    }

    if (restrictToUserFolder && path !== '/' && path !== '~' && !isPathAllowed(path)) {
      setError(t('folder_browser.error_access_denied'));
      setLoading(false);
      return;
    }
    
    setLoading(true);
    // setError(null);
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ command: 'listDirectory', path }));
    }
  }, [ws, restrictToUserFolder, isPathAllowed, t]);

  const handleSelect = useCallback((selectedPath) => {
    // Use displayPath for UI-facing consumers when restricted; fall back to actual path otherwise
    const outgoingPath = restrictToUserFolder ? (displayPath || selectedPath || currentPath) : (selectedPath || currentPath);
    onSelect(outgoingPath);
    onClose();
  }, [onSelect, onClose, currentPath, restrictToUserFolder, displayPath]);

  // Shared navigation/validation logic
  const tryNavigateToInputPath = (newPath) => {
    const resolvedPath = resolveDisplayPath(newPath);
    if (resolvedPath !== currentPath) {
      // Validate path if restricted
      if (restrictToUserFolder && !isPathAllowed(resolvedPath)) {
        setError(t('folder_browser.error_access_denied'));
        setLoading(false);
        setIsValidatingPath(false);
        return;
      }
      
      setIsValidatingPath(true);
      setLoading(true);
      setError(null);
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ command: 'listDirectory', path: resolvedPath }));
      }
    }
  };

  // Called when exiting edit mode (from Breadcrumb)
  const handleExitEditMode = (inputValue) => {
    tryNavigateToInputPath(inputValue);
  };

  // Select button handler
  const validateAndSelectPath = () => {
    if (allowFileSelection && selectedFile) {
      const fullFilePath = buildSelectedFilePath();
      if (fullFilePath) {
        onSelect(fullFilePath);
        onClose();
        return;
      }
    }
    const resolvedInputPath = resolveDisplayPath(inputPath);
    if (resolvedInputPath === currentPath) {
      handleSelect(currentPath);
      return;
    }
    setPendingSelect(true);
    tryNavigateToInputPath(resolvedInputPath);
  };

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      const data = parsedMessage.data || {};
      
      if (parsedMessage.command === 'listDirectory') {
        const allItems = data.folders || [];
        const incomingPath = data.currentPath || '';
        const incomingDisplayPath = data.displayPath || incomingPath;
        
        // Normalize folder entries to strings (handle objects or strings)
        let normalizedFolders = allItems
          .map(f => {
            if (typeof f === 'string') return f;
            if (f && typeof f === 'object') return f.name || f.path || '';
            return '';
          })
          .filter(Boolean);

        // Filter folders based on restrictToUserFolder
        let filteredFolders = normalizedFolders;
        
        if (restrictToUserFolder && allowedRoots && allowedRoots.length > 0) {
          // If at root level (showing drives), hide everything since we should never show root
          if (data.currentPath === '/' || data.currentPath === '~') {
            filteredFolders = [];
            // Instead, navigate directly to first allowed root
            if (allowedRoots.length > 0 && ws && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ command: 'listDirectory', path: allowedRoots[0] }));
              return;
            }
          }
        }
        
        setFolders(filteredFolders);
        setCurrentPath(incomingPath);
        setDisplayPath(incomingDisplayPath);
        setInputPath(incomingDisplayPath);
        setLoading(false);

        if (data.error) {
          setError(t('folder_browser.error_list_directory'));
          setIsValidatingPath(false);
          setPendingSelect(false);
        } else {
          setError(null);
          if (isValidatingPath && pendingSelect) {
            setIsValidatingPath(false);
            setPendingSelect(false);
            handleSelect(incomingPath);
          }
          setIsValidatingPath(false);
        }

        // Request files in this directory
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ 
            command: 'listFilesInDirectory', 
            path: data.currentPath || '' 
          }));
        }
      } else if (parsedMessage.command === 'listFilesInDirectory') {
        const allFiles = data.files || [];
        // Normalize file entries to strings (handle objects or strings)
        const normalizedFiles = allFiles
          .map(f => {
            if (typeof f === 'string') return f;
            if (f && typeof f === 'object') return f.name || f.fileName || f.filename || '';
            return '';
          })
          .filter(Boolean);

        if (restrictToUserFolder && data.displayPath !== undefined) {
          setDisplayPath(data.displayPath);
          setInputPath(data.displayPath);
        }
        setSelectedFile(null);
        setFiles(normalizedFiles);
      } else if (parsedMessage.command === 'createFolder') {
        if (data.error) {
          setError(t('folder_browser.error_create_directory'));
        } else if (data.success) {
          setError(null);
          setCurrentPath(data.path);
          listDirectory(data.path);
        }
      } else if (parsedMessage.command === 'renameFolder') {
        if (data.error) {
          setError(t('folder_browser.error_rename_directory'));
        } else if (data.success) {
          setError(null);
          setCurrentPath(data.newPath);
          listDirectory(data.newPath);
        }
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, ws, currentPath, isValidatingPath, pendingSelect, handleSelect, listDirectory, t, allowedRoots, restrictToUserFolder]);


  const handleFolderClick = (folder) => {
    // If at root and folder is a drive (e.g., 'D:'), just send 'D:'
    if (currentPath === '/' && /^[A-Za-z]:$/.test(folder)) {
      listDirectory(folder);
    } else {
      const newPath = currentPath === '/' ? `/${folder}` : `${currentPath}/${folder}`;
      listDirectory(newPath);
    }
  };

  const handleUpClick = () => {
    // If restricted, check if we're at an allowed root boundary
    if (restrictToUserFolder && allowedRoots && allowedRoots.length > 0) {
      const normalizePath = (p) => p.replace(/\\/g, '/').toLowerCase();
      const normalizedCurrent = normalizePath(currentPath);
      
      // Check if current path matches any allowed root
      const isAtAllowedRoot = allowedRoots.some(root => {
        const normalizedRoot = normalizePath(root);
        return normalizedCurrent === normalizedRoot;
      });
      
      if (isAtAllowedRoot) {
        setError(null);
        setLoading(false);
        return;
      }
    }
    
    // Split on both / and \\ for cross-platform compatibility
    const parts = currentPath.split(/[\\/]/).filter(Boolean);
    const isWindows = typeof navigator !== 'undefined' && /windows/i.test(navigator.userAgent);
    
    // If restricted, never allow going to root
    if (restrictToUserFolder) {
      if (parts.length === 0) {
        setError(null);
        setLoading(false);
        return;
      }
      // If Windows drive root (e.g., C:), don't go to '/'
      if (isWindows && parts.length === 1 && /^[A-Za-z]:$/.test(parts[0])) {
        setError(null);
        setLoading(false);
        return;
      }
    }
    
    if (parts.length === 0) {
      listDirectory('/');
      return;
    }
    // If Windows drive root (e.g., C:), go to '/'
    if (isWindows && parts.length === 1 && /^[A-Za-z]:$/.test(parts[0])) {
      listDirectory('/');
      return;
    }
    // If Unix and at root
    if (!isWindows && parts.length === 0) {
      listDirectory('/');
      return;
    }
    let parentPath;
    if (isWindows) {
      // If first part is a drive letter, join with '\\', else join with '/'
      if (/^[A-Za-z]:$/.test(parts[0])) {
        if (parts.length === 1) {
          parentPath = parts[0];
        } else {
          parentPath = parts.slice(0, -1).join('\\');
        }
      } else {
        parentPath = parts.slice(0, -1).join('\\');
      }
    } else {
      // Unix: always join with '/' and prepend '/'
      parentPath = '/' + parts.slice(0, -1).join('/');
    }
    
    // Check if navigating up would go outside allowed roots
    if (restrictToUserFolder && !isPathAllowed(parentPath)) {
      setError(null);
      setLoading(false);
      return;
    }
    
    listDirectory(parentPath === '/' ? '/' : parentPath);
  };

  const handleAddFolder = () => {
    setIsAddingFolder(true);
    setNewFolderName('New Folder');
    setTimeout(() => {
      if (newFolderInputRef.current) {
        newFolderInputRef.current.focus();
        newFolderInputRef.current.select();
      }
    }, 0);
  };

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ command: 'createFolder', path: currentPath, name: newFolderName }));
    }
    setIsAddingFolder(false);
  };

  const handleCancelAddFolder = () => {
    setIsAddingFolder(false);
  };

  const handleNewFolderKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCreateFolder();
    } else if (e.key === 'Escape') {
      setIsAddingFolder(false);
      setNewFolderName('New Folder');
    }
  };

  const handleEditFolder = (folder) => {
    setEditingFolder(folder);
    setEditFolderName(folder);
    setTimeout(() => {
      if (editFolderInputRef.current) {
        editFolderInputRef.current.focus();
        editFolderInputRef.current.select();
      }
    }, 0);
  };

  const handleRenameFolder = () => {
    if (!editFolderName.trim() || editFolderName === editingFolder) {
      setEditingFolder(null);
      return;
    }
    console.log("renaming folder to:", editFolderName)
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        command: 'renameFolder',
        path: currentPath,
        oldName: editingFolder,
        newName: editFolderName
      }));
    }
    setEditingFolder(null);
  };

  const handleEditFolderKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleRenameFolder();
    } else if (e.key === 'Escape') {
      setEditingFolder(null);
    }
  };

  // Filter folders based on showHidden
  const visibleFolders = folders.filter(folder => showHidden || !folder.startsWith('.'));
  // Filter files based on showHidden
  const visibleFiles = files.filter(file => showHidden || !file.startsWith('.'));

  // Helper to get the current folder name
  const getCurrentFolderName = () => {
    if (!currentPath || currentPath === '/' || currentPath === '~') return '';
    // Split on both / and \
    const parts = currentPath.split(/[\\/]/).filter(Boolean);
    return parts[parts.length - 1] || '';
  };

  // Helper to check if we are at the root
  const isAtRoot = currentPath === '/' || currentPath === '~' || currentPath === '';

  // Update the Breadcrumb component to pass input value
  const handlePathChange = (newPath) => {
    setInputPath(newPath);
  };

  const buildSelectedFilePath = useCallback(() => {
    if (!selectedFile) return '';
    const base = displayPath || currentPath || '';
    const separator = base.includes('\\') ? '\\' : '/';
    const trimmedBase = base.endsWith(separator) ? base.slice(0, -1) : base;
    return `${trimmedBase}${separator}${selectedFile}`;
  }, [selectedFile, displayPath, currentPath]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '400px',
          maxHeight: '600px'
        }
      }}
    >

      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.primaryDark} 100%)`,
        color: themeColors.lightText,
        fontWeight: 500,
        fontSize: '1.1rem',
        letterSpacing: 0.5,
        // minHeight: 56
      }}>
        {t('folder_browser.title')}
        <CloseButton onClick={onClose} size="small" sx={{ color: themeColors.lightText }} />
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {/* <FormControlLabel
          label={t('folder_browser.show_hidden')}
          control={
            <Switch
              checked={showHidden}
              onChange={e => setShowHidden(e.target.checked)}
              color="primary"
            />
          }          
          sx={{ mb: 1, alignSelf: 'flex-end' }}
        /> */}
        <div className="mb-4">
          <Breadcrumb
            path={displayPath || currentPath}
            onNavigate={listDirectory}
            onAddFolder={handleAddFolder}
            onPathChange={handlePathChange}
            onExitEditMode={handleExitEditMode}
            displayRoot={displayRoot}
          />
        </div>
        {error && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              mb: 2,
              bgcolor: '#ffebee',
              border: '1px solid #ffcdd2',
              borderRadius: 1,
              color: '#d32f2f'
            }}
          >
            <Typography>{error}</Typography>
            <CloseButton
              size="small"
              onClick={() => setError(null)}
              sx={{ color: '#d32f2f' }}
            />
          </Box>
        )}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: 1,
            overflow: 'hidden'
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {isAddingFolder && (
                <ListItem
                  sx={{ borderBottom: visibleFolders.length > 0 ? '1px solid rgba(0, 0, 0, 0.12)' : 'none', background: '#e3f2fd' }}
                  disableGutters
                >
                  <ListItemIcon>
                    <FolderIcon color="primary" sx={{ paddingLeft: 2 }} />
                  </ListItemIcon>
                  <InputBase
                    inputRef={newFolderInputRef}
                    value={newFolderName}
                    onChange={e => setNewFolderName(e.target.value)}
                    onKeyDown={handleNewFolderKeyDown}
                    sx={{ fontWeight: 500, fontSize: '1rem', flex: 1, ml: 1 }}
                  />
                  <CloseButton size="small" onClick={handleCancelAddFolder} color="error" />
                  <IconButton size="small" onClick={handleCreateFolder}>
                    <AddIcon color="success" />
                  </IconButton>
                </ListItem>
              )}
              {!isAtRoot && (
                <ListItem
                  sx={{
                    borderBottom: visibleFolders.length > 0 ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                  disableGutters
                  onClick={handleUpClick}
                >
                  <IconButton size="small" sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                  </IconButton>
                  {/* <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                    {getCurrentFolderName()}
                  </Typography> */}
                </ListItem>
              )}
              {visibleFolders.map((folder, index) => (
                <ListItem
                  key={folder}
                  disablePadding
                  sx={{
                    borderBottom: index < visibleFolders.length - 1 ? '1px solid rgba(0, 0, 0, 0.12)' : 'none'
                  }}
                >
                  {editingFolder === folder ? (
                    <ListItem
                      sx={{
                        borderBottom: visibleFolders.length > 0 ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                        background: '#e3f2fd'
                      }}
                      disableGutters
                    >
                      <ListItemIcon>
                        <FolderIcon color="primary" />
                      </ListItemIcon>
                      <InputBase
                        inputRef={editFolderInputRef}
                        value={editFolderName}
                        onChange={e => setEditFolderName(e.target.value)}
                        onKeyDown={handleEditFolderKeyDown}
                        sx={{ fontWeight: 500, fontSize: '1rem', flex: 1, ml: 1 }}
                      />
                      <IconButton size="small" onClick={handleRenameFolder}>
                        <CheckIcon color="success" />
                      </IconButton>
                    </ListItem>
                  ) : (
                    <ListItemButton
                      onClick={() => handleFolderClick(folder)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <FolderIcon />
                      </ListItemIcon>
                      <ListItemText primary={folder} />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditFolder(folder);
                        }}
                        sx={{
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          '.MuiListItemButton-root:hover &': {
                            opacity: 1
                          }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
        {/* Files preview list */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid rgba(0, 0, 0, 0.08)',
            borderRadius: 1,
            background: 'rgba(245,245,245,0.4)',
            mt: 1,
            p: 1,
            minHeight: 40
          }}
        >
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
            {t('folder_browser.files_in_this_folder')}
          </Typography>
          {visibleFiles.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              {t('folder_browser.no_files_in_this_folder')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {visibleFiles.map((file, idx) => (
                <ListItemButton
                  key={idx}
                  sx={{
                    pl: 2,
                    borderRadius: 1,
                    mb: 0.5,
                    bgcolor: allowFileSelection && selectedFile === file ? 'rgba(87, 87, 176, 0.08)' : 'transparent'
                  }}
                  onClick={() => {
                    if (!allowFileSelection) return;
                    setSelectedFile(file);
                    // Update the input path to reflect the file selection for clarity
                    const base = displayPath || currentPath || '';
                    const separator = base.includes('\\') ? '\\' : '/';
                    const trimmedBase = base.endsWith(separator) ? base.slice(0, -1) : base;
                    setInputPath(`${trimmedBase}${separator}${file}`);
                  }}
                  onDoubleClick={() => {
                    if (!allowFileSelection) return;
                    setSelectedFile(file);
                    const fullPath = buildSelectedFilePath();
                    if (fullPath) {
                      onSelect(fullPath);
                      onClose();
                    }
                  }}
                  disabled={!allowFileSelection}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <InsertDriveFileIcon fontSize="small" color="disabled" />
                  </ListItemIcon>
                  <ListItemText primary={file} />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>
      </DialogContent>

      <DialogActions sx={{
        p: 2,
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        backgroundColor: 'rgba(245, 245, 245, 0.5)',
        backdropFilter: 'blur(5px)'
      }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              color: themeColors.primary,
              borderColor: themeColors.primary,
              '&:hover': {
                borderColor: themeColors.primaryLight,
                backgroundColor: 'rgba(87, 87, 176, 0.04)'
              },
              borderRadius: 2,
              px: 3
            }}
          >
            {t('folder_browser.cancel')}
          </Button>
          <Button
            variant="contained"
            onMouseDown={() => {
              setPendingSelect(true);
            }}
            onClick={validateAndSelectPath}
            disabled={loading || error || isValidatingPath}
            sx={{
              backgroundColor: themeColors.primary,
              color: themeColors.lightText,
              '&:hover': { backgroundColor: themeColors.primaryLight },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(87, 87, 176, 0.4)',
                color: 'rgba(255, 255, 255, 0.6)'
              },
              borderRadius: 2,
              px: 3,
              ml: 2,
              boxShadow: '0 4px 10px rgba(87, 87, 176, 0.3)',
              transition: 'all 0.3s ease',
              '&:not(.Mui-disabled):hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 12px rgba(87, 87, 176, 0.4)',
              },
            }}
          >
            {loading || isValidatingPath ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('folder_browser.select')
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default FolderBrowser; 
